import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Platform } from '@angular/cdk/platform';
import { Router } from '@angular/router';
import { Observable, ReplaySubject, throwError } from 'rxjs';
import { catchError, map, retry, shareReplay, timeout } from 'rxjs/operators';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { v4 as uuidv4 } from 'uuid';
import * as CryptoJS from 'crypto-js';
import { filter as lodashFilter } from 'lodash';
import * as moment from 'moment';

import { environment } from '@environments/environment';
import Resources from '@constants/resourses';

import { IAPIErrorRequest, IErrorHandling } from '../models/utility.interface';
import { IQuotesTariff } from '../models/quotesTariff.interface';
import { AlertDialogData } from '@shared/dialog/alert-dialog/alert-dialog.interface';

import { MessagepopupComponent } from '@shared/dialog/messagepopup/messagepopup.component';
import { FindmoreDialogComponent } from '@shared/dialog/findmore-dialog/findmore-dialog.component';
import { ErrorHandlingDialogComponent } from '@shared/dialog/error-handling-dialog/error-handling-dialog.component';
import { TariffDetailsDialogComponent } from '@shared/dialog/tariff-details-dialog/tariff-details-dialog.component';
import { AlertDialogComponent } from '@shared/dialog/alert-dialog/alert-dialog.component';

import { RestapiService } from './restapi.service';
import { SessionService } from './session.service';
import { LoginComponent } from '@shared/dialog/login/login.component';
import { TealiumUtagService } from './utag.service';
import { MessagepopupSimpleComponent } from '@shared/dialog/messagepopup-simple/messagepopup-simple.component';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  private $status: ReplaySubject<any> = new ReplaySubject<any>(5);
  private cachedDefaultSupplierConfig: Observable<any> | null = null;

  constructor(
    private platform: Platform,
    private restAPI: RestapiService,
    private sessionService: SessionService,
    private breakpointObserver: BreakpointObserver,
    private http: HttpClient,
    private router: Router,
    private dialog: MatDialog,
    private tealiumService: TealiumUtagService
  ) { }

  // ---------------------- Application Status Observable --------------------------
  /**
  * @description - Get the updated status from the observable
  * @returns - Observable<any>
  * @example getStatus
  */
  getStatus: Observable<any> = this.$status.asObservable();
  /**
   * @description - Notify the status to all the subscribers
   * @param status - Status to be updated
   * @example updateStatus('load')
   */
  updateStatus = (status) => this.$status.next(status);
  /**
   * @description - Reset the status to initial state
   * @example resetStatus()
   */
  resetStatus = () => {
    this.updateStatus('');
    this.updateStatus('');
    this.updateStatus('');
    this.updateStatus('');
    this.updateStatus('');
  };

  getDefaultSupplierConfiguration(): Observable<any> {
    if (!this.cachedDefaultSupplierConfig) {
      const payload = {
        supplierId: 10000000,
        isSupplier: false,
        switchMode: 'acquisition'
      };
      this.cachedDefaultSupplierConfig = this.getConfiguration(payload).pipe(
        // Share the same observable for multiple subscribers and cache the last emission
        shareReplay(1)
      );
    }
    return this.cachedDefaultSupplierConfig;
  }

  // Get validation configuration
  getConfiguration(bodyObj): Observable<any> {
    return this.http.post<any>(`${environment.api_url}/v3/supplier/supplierConfig`, bodyObj, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'e_api_key': environment.resource_identifier
      })
    }).pipe(
      timeout(30000),
      retry(1)
    );
  }

  /**
   * @description Extracts the domain from an email string
   * @param email string
   * @returns string
   */
  getEmailDomain(email: string): string {
    const atIndex = email.indexOf('@');
    if (atIndex === -1) {
      return email; // Return the original string if '@' is not found
    }
    return email.substring(atIndex + 1);
  }

  /**
   * @description Obfuscates an email address by showing only the first and last character of the local part
   * @param email string - The email address to obfuscate
   * @returns string - The obfuscated email (e.g., "j***e@example.com")
   */
  getObfuscatedEmail(email: string): string {
    if (!email || typeof email !== 'string') {
      return email;
    }

    const atIndex = email.indexOf('@');
    if (atIndex === -1) {
      return email; // Return the original string if '@' is not found
    }

    const localPart = email.substring(0, atIndex);
    const domain = email.substring(atIndex);

    if (localPart.length <= 2) {
      // If local part has 2 or fewer characters, just mask with asterisks
      return '*'.repeat(localPart.length) + domain;
    }

    // Show first and last character, mask the middle with asterisks
    const firstChar = localPart.charAt(0);
    const lastChar = localPart.charAt(localPart.length - 1);
    const middleLength = localPart.length - 2;

    return firstChar + '*'.repeat(middleLength) + lastChar + domain;
  }

  /**
   * @description - Checks if the object is empty or not
   * @param obj - Object to be checked
   * @returns  - boolean
   */
  isObjectEmpty(obj: any): boolean {
    return (Object.keys(obj).length === 0);
  }

  /**
   * @description - Encrypts the text into SHA256
   * @param text - text to be encrypted
   * @returns - encrypted text
   */
  encryptIntoSha256(text: string): any {
    return CryptoJS.SHA256(text).toString();
  }

  /**
   * @description - Encrypts the data into AES
   * @param data - data to be encrypted
   * @returns - encrypted data
   * @example encryptData({ key: 'value' }) => 'U2FsdGVkX1+...'
   */
  encryptData(data: any): string {
    const key = 'new Date()';
    const iv = '1234567890123456'; // 16 bytes IV
    const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(data), key, {
      iv: CryptoJS.enc.Utf8.parse(iv),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    return encryptedData.toString();
  }

  /**
   * @description - Decrypts the data into AES
   * @param encryptedData - data to be decrypted
   * @returns - decrypted data
   * @example decryptData('U2FsdGVkX1+...') => { key: 'value' }
   */
  decryptData(encryptedData: string): any {
    const key = 'new Date()';
    const iv = '1234567890123456'; // 16 bytes IV
    const decryptedData = CryptoJS.AES.decrypt(encryptedData, key, {
      iv: CryptoJS.enc.Utf8.parse(iv),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    const decryptedText = decryptedData.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedText);
  }

  /**
   * @description - Sends an email to the team when an API fails
   * @param subject - Subject of the email
   * @param payload - Payload of the api failure request
   * @param response - Response of the api failure request
   * @param responseTime - Time taken by the api to respond
   * @param isSpendEntered -  Whether the spend is entered or not
   * @returns - void
   */
  sendAPIError(subject: string, payload: any, response: any, responseTime: number, isSpendEntered?: boolean): void {
    const bodyObj: IAPIErrorRequest = {
      subject: environment.ENV + ' - ' + subject,
      payload: payload,
      response: response,
      correlationId: this.sessionService.getItemFromSession(Resources.SessionStorageKeys.CorrelationId),
      address: this.sessionService.getItemFromSession(Resources.SessionStorageKeys.SelectedAddress),
      ipAddress: this.sessionService.getItemFromSession(Resources.SessionStorageKeys.IpAddress),
      platform: this.platform,
      browserVersion: '', // Not in use
      'isTrusted-retry': false, // Not in use
      errorCount: 1,
      timeTaken: responseTime,
      idToken: this.sessionService.getItemFromSession(Resources.SessionStorageKeys.AuthorizeIdToken),
      uuidToken: uuidv4(),
      source: window.location.href,
    };

    // Is spend entered
    if (isSpendEntered) {
      bodyObj.isSpendEntered = !!isSpendEntered;
    }

    // Send email to the team
    this.http.post(environment.email_api + 'cron/api-failure-report', bodyObj, { headers: { 'Content-Type': 'application/json', 'e_api_key': environment.resource_identifier } }).subscribe();
  }

  /**
   * @description - Formats the number to 2 decimal places
   * @param num - Number to be formatted
   * @returns - Formatted number
   */
  formatNumber(num: number): number {
    const factor = Math.pow(10, 2);
    return Math.round((num + Number.EPSILON) * factor) / factor;
  }

  /**
  * Smart number formatting logic:
  * - If number is 1.5, show 1.50
  * - If number is 2, show 2 (not 2.00)
  * - If number is 1.3333333, show 1.33
  */
  smartFormatNumber(value: number): string {
    if (value == null || isNaN(value)) {
      return '0';
    }

    // Round to 2 decimal places
    const rounded = Math.round((value + Number.EPSILON) * 100) / 100;

    // Check if it's a whole number
    if (rounded % 1 === 0) {
      return rounded.toString(); // Return whole number without decimals (e.g., "2")
    }

    // Check if it has only one decimal place
    const oneDecimal = Math.round((value + Number.EPSILON) * 10) / 10;
    if (Math.abs(rounded - oneDecimal) < 0.001) {
      return oneDecimal.toFixed(2); // Show with .X0 format (e.g., "1.50")
    }

    // For numbers with 2+ decimal places, show 2 decimal places
    return rounded.toFixed(2); // Show with .XX format (e.g., "1.33")
  }

  /**
   * @description Check if the user is logged in or not
   * @returns boolean
   */
  isLoggedIn(): boolean {
    return this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Email) && this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.IsConsentApproval) && this.sessionService.getItemFromSession(Resources.SessionStorageKeys.IsAccessToken);
  }

  /**
   * @description User wants to sign out
   */
  signingOut(): void {
    // Removed session specific data
    this.sessionService.clearSessionStorage();
    // Removed email and consent approval from local storage [AT removed from session storage]
    this.sessionService.removeItemFromLocal(Resources.SessionStorageKeys.Email);
    this.sessionService.removeItemFromLocal(Resources.SessionStorageKeys.IsConsentApproval);
    // Removed quotes data from local storage
    this.sessionService.removeItemFromLocal(Resources.SessionStorageKeys.QuotesData);

    // Navigate to the home URL
    this.router.navigate(['/']);
  }

  /**
   * @description Get markdown file from S3 to display content
   * @param fileName | string
   * @returns Observable<any>
   * @example getMDFile('terms-and-conditions.md')
   */
  getMDFile(fileName: string): Observable<any> {
    return this.restAPI.getS3FileAPI(fileName)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((error: any) => {
          console.error('Error fetching file:', error);
          return throwError(error);
        })
      )
  }

  /**
   * @description - Masking the email address
   * @param email - email to be masked
   * @returns - masked email address
   */
  maskingEmail(email: string): string {
    // If email is empty, return empty string
    if (!email) return '';

    // email should be in the format of '****@domain'
    const [name, domain] = email.split('@');
    const maskName = name[0] + name[1] + '******';
    return maskName + '@' + domain;
  }

  /**
   * 
   * @param postcode - Postcode to be trimmed
   * @description - Trims the postcode to 2, 3 or 4 characters based on the length of the postcode
   * @returns - trimmed postcode
   * @example partialPostcode('1234567') => '1234'
   */
  partialPostcode(postcode: string): string {
    postcode = postcode?.replace(/\s/g, ''); // Remove spaces
    if (postcode?.length === 5) {
      return postcode?.substring(0, 2);
    } else if (postcode?.length === 6) {
      return postcode?.substring(0, 3);
    } else if (postcode?.length === 7) {
      return postcode?.substring(0, 4);
    }
  }

  // [TEAM] - Get IP address usually get from backend from req.ip instead of using this API
  /**
   * @description - Get the IP address of the user
   * @returns - Observable<any>
   * @example getIPAddress()
   */
  getIPAddress(): Observable<any> {
    return this.http.get("https://api.ipify.org?format=json")
      .pipe(
        map((response: any) => {
          return response;
        }), catchError((error: any) => {
          console.error('Error fetching IP address:', error);
          return throwError(error);
        })
      );
  }

  /**
   * @description - Get the fuel type based on the target
   * @param data - Data to be checked
   * @param target - Target to be checked [current-usage, saved-detail]
   * @returns - Fuel type ['B', 'E', 'G']
   * @example getFuelType(data, 'current-usage')
   */
  getFuelType(data: any, target: string): string {
    // Current usage
    if (target === 'current-usage') {
      return data?.electricity && data?.gas ? Resources.EnergyType.BOTH : data?.electricity ? Resources.EnergyType.ELECTRICITY : Resources.EnergyType.GAS;
    } else if (target === 'saved-detail') {
      return data?.switchDetails?.switchData?.[0]?.fuelType || (data?.meterDetails?.fuelType === 'BOTH' ? Resources.EnergyType.BOTH : data?.meterDetails?.fuelType === 'ELECTRICITY' ? Resources.EnergyType.ELECTRICITY : Resources.EnergyType.GAS);
    }
  }

  openSimpleMessageDialog(title: string, content: string[]): MatDialogRef<MessagepopupSimpleComponent> {
    return this.dialog.open(MessagepopupSimpleComponent, {
      data: {
        title,
        content
      },
      maxWidth: window.innerWidth <= 767 ? '95vw' : '600px',
      disableClose: true,
      autoFocus: false,
      panelClass: 'ctm-modal'
    });
  }

  /**
   * @description - Open the message file in a dialog
   * @param fileName - File name to be opened or dialog configuration for tariff details
   * @param target - Optional target to specify which dialog component to use
   * @returns void or MatDialogRef based on target
   * @example openMessageDialog('terms-and-conditions.md')
   * @example openMessageDialog('privacy-policy.md')
   * @example openMessageDialog('how-it-works.md', 'findOutMore')
   * @example openMessageDialog(dialogConfig, 'tariffDetails')
   */
  openMessageDialog(fileName: string | any, target?: string): void | MatDialogRef<any> {
    // Find out more dialog
    if (target === 'findOutMore') {
      return this.dialog.open(FindmoreDialogComponent, {
        data: {
          key: fileName
        },
        maxWidth: window.innerWidth <= 767 ? '95vw' : '600px',
        disableClose: true,
        autoFocus: false,
        panelClass: 'ctm-modal'
      });
    } else if (target === 'tariffDetails') {
      // For tariff details, we expect fileName to be a dialog configuration object
      return this.dialog.open(TariffDetailsDialogComponent, fileName);
    } else {
      return this.dialog.open(MessagepopupComponent, {
        data: {
          key: fileName
        },
        maxWidth: window.innerWidth <= 767 ? '95vw' : '600px',
        disableClose: true,
        autoFocus: false,
        panelClass: 'ctm-modal'
      });
    }
  }

  /**
   * @description - Open the error handling dialog [Type: error | retry]
   * @param errorDetails - Error details to be handled
   * @returns MatDialogRef<ErrorHandlingDialogComponent>
   * @example errorHandlingDialog({ error: 'Error message', title: 'Error title', type: 'error', message: 'Error message' })
   */
  errorHandlingDialog(errorDetails: IErrorHandling): MatDialogRef<ErrorHandlingDialogComponent> {
    return this.dialog.open(ErrorHandlingDialogComponent, {
      data: errorDetails,
      maxWidth: window.innerWidth <= 767 ? '95vw' : '600px',
      disableClose: true,
      autoFocus: false,
      panelClass: 'ctm-modal'
    });
  }

  /**
   * @description - Navigate to the given page
   * @param page - Page to be opened
   * @example navigateTo('home')
   * @example navigateTo('https://www.example.com')
   * @example navigateTo('https://www.example.com?param1=value1&param2=value2')
   */
  navigateTo(page: string): void {
    window.open(this.router.serializeUrl(
      this.router.createUrlTree([page])
    ), '_blank');
  }

  /**
   * @description - Set a cookie with the given name, value and expiry days
   * @param name - Cookie name
   * @param value - Cookie value
   * @param hours - Number of hours until cookie expiry
   */
  setCookie(name: string, value: string, hours: number): void {
    const date = new Date();
    date.setTime(date.getTime() + (hours * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/`;
  }

  /**
   * @description - Get a cookie value by its name
   * @param name - Cookie name
   * @returns - Cookie value or null if not found
   */
  getCookie(name: string): string | null {
    const cookieName = `${name}=`;
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }

    return null;
  }

  /**
   * @description - Get the breakpoint for the given screen size
   * @returns - returns observable of boolean
   * @example getBreakpoint()
   */
  getBreakpoint(): any {
    return this.breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.TabletPortrait])
      .pipe(map(result => result.matches));
  }

  /**
   * @description - Normalise the value to boolean or string
   * @param value - Value to be normalised
   * @returns - boolean | string
   * @example normaliseBooleanOrString('true') => true
   * @example normaliseBooleanOrString('false') => false
   * @example normaliseBooleanOrString('some string') => 'some string'
   */
  normaliseBooleanOrString(value: any): boolean | string {
    try {
      return value = JSON.parse(value);
    } catch (e) {
      // If parsing fails, keep the value as is string
      return value;
    }
  }

  /**
   * @description - Find quotes by filter key in the given array of quotes
   * @param quotes - Array of quotes to be filtered
   * @param filterKey - Key to filter by (e.g. { supplierId: 123 })
   * @returns - Array of filtered quotes
   * @example findQuoteByFilterKey(quotes, { supplierId: 123 })
   */
  findQuoteByFilterKey(quotes: IQuotesTariff[], filterKey: any): IQuotesTariff[] {
    if (!quotes || !filterKey) return [];

    try {
      return lodashFilter(quotes, filterKey);
    } catch (error) {
      console.error('Error filtering quotes:', error);
      return [];
    }
  }

  /**
   * @description - Calculate total months from today to a given date
   * @param date - Target date in string format (e.g. "2023-12-31")
   * @param type - Type of calculation ('month' for months, 'day' for days)
   * @returns - Number of months/days between today and the target date
   * @example getTotalMonthFromToday("2023-12-31", "month") => 6
   */
  getTotalMonthFromToday(date: string, type: string): number {
    if (!date || date === 'N/A') return 0;

    try {
      const today = moment();
      const targetDate = moment(date);

      if (!targetDate.isValid()) return 0;

      if (type === 'month') {
        // Calculate the difference in months
        return targetDate.diff(today, 'months');
      } else {
        // Calculate the difference in days
        return targetDate.diff(today, 'days');
      }
    } catch (error) {
      console.error('Error calculating months from today:', error);
      return 0;
    }
  }

  /**
   * @description - Check if a number is negative
   * @param num - Number to check
   * @returns - Boolean indicating if the number is negative
   * @example isNegativeNumber(-1) => true
   * @example isNegativeNumber(1) => false
   */
  isNegativeNumber(num: number): boolean {
    return num < 0;
  }

  /**
     * @description - Alert about the error and provide an option to try again
     * @example tryAgain()
     * @returns - void
     */
  tryAgain(): void {
    const alertDialogData: AlertDialogData = {
      pageName: 'Retry Page',
      title: 'Retry after some time',
      message: 'We are unable to process your request at the moment. Please try again later.',
      buttonText: 'Go to dashboard'
    };
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: alertDialogData,
      maxWidth: window.innerWidth <= 767 ? '95vw' : '600px',
      disableClose: true,
      autoFocus: false,
      panelClass: 'ctm-modal'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.IsNotNow, true);
        this.resetStatus();
        // Navigate to home page
        this.router.navigate(['/information']);
      }
    });
  }

  // signIn method
  signIn(): void {
    const tealiumData = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.TealiumData) || {};
    const dialogRef = this.dialog.open(LoginComponent, {
      data: { placement: 'content_sign_in', target: 'login' },
      width: '570px',
      maxWidth: window.innerWidth <= 767 ? '95vw' : '569px',
      disableClose: true,
      autoFocus: false,
      panelClass: 'ctm-modal'
    });

    // After the dialog is closed, check the result
    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        // Closed loggedIn popup
        const isLoggedIn = this.isLoggedIn();
        const email = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Email);
        const selectedQuote = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.SelectedQuote);

        // From postcode page
        if (window.location.pathname === '/') {
          tealiumData['page_name'] = 'PostcodeEntry';
          tealiumData['page_type'] = 'RiskJourney';

          delete tealiumData['energy_supplier_switching_to'];
          delete tealiumData['energy_electricitytariff_switching_to'];
          delete tealiumData['energy_gastariff_switching_to'];
        } else if (window.location.pathname === '/information') {
          // From information page
          tealiumData['page_name'] = 'YourEnergyDashboard';
          tealiumData['page_type'] = 'RiskJourney';

          delete tealiumData['energy_supplier_switching_to'];
          delete tealiumData['energy_electricitytariff_switching_to'];
          delete tealiumData['energy_gastariff_switching_to'];
        } else if (window.location.pathname === '/energy-result') {
          // From quote page
          tealiumData['page_name'] = 'YourTariffs';
          tealiumData['page_type'] = 'EnquiryPresentation';

          delete tealiumData['energy_supplier_switching_to'];
          delete tealiumData['energy_electricitytariff_switching_to'];
          delete tealiumData['energy_gastariff_switching_to'];
        } else if (window.location.pathname === '/switch/user-details') {
          // Application page
          tealiumData['page_name'] = 'SwitchApplication';
          tealiumData['page_type'] = 'PaymentPage';
          tealiumData['energy_supplier_switching_to'] = selectedQuote.SupplierName;
          tealiumData['energy_electricitytariff_switching_to'] = selectedQuote?.TariffName;
          tealiumData['energy_gastariff_switching_to'] = selectedQuote?.TariffName;
        } else if (window.location.pathname === '/switch/confirmation') {
          // Switch confirmation page
          tealiumData['page_name'] = 'SwitchRequestConfirmed';
          tealiumData['page_type'] = 'PaymentPage';

          delete tealiumData['energy_supplier_switching_to'];
          delete tealiumData['energy_electricitytariff_switching_to'];
          delete tealiumData['energy_gastariff_switching_to'];
        }

        delete tealiumData['enquirySubject'];
        delete tealiumData['encodedEmail'];
        tealiumData['customer_id'] = (!!email) ? this.encryptIntoSha256(email.toLowerCase().trim()) : undefined;
        tealiumData['base_url'] = window.location.href;
        tealiumData['energycomparison_subscribed'] = !!email;

        // Trigger view event
        this.tealiumService.viewEvent(tealiumData);
      }
    });
  }

}